import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, ShieldCheck, AlertTriangle, ImageOff, Trash2, Repeat } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import RefillDialog from "../components/RefillDialog";
import LabFromRxCTA from "../components/LabFromRxCTA";
import SendbirdEmbeddedChannel from "../components/sendbird/SendbirdEmbeddedChannel";
import { api, getToken } from "../api/client";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthedImage = ({ id, alt = "" }) => {
    const [src, setSrc] = useState(null);
    const [err, setErr] = useState(false);

    useEffect(() => {
        let active = true;
        let blobUrl = null;
        (async () => {
            try {
                const r = await fetch(`${BACKEND_URL}/api/prescriptions/${id}/image`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                if (!r.ok) throw new Error();
                const blob = await r.blob();
                blobUrl = URL.createObjectURL(blob);
                if (active) setSrc(blobUrl);
            } catch {
                if (active) setErr(true);
            }
        })();
        return () => {
            active = false;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [id]);

    if (err) return <div className="flex aspect-[4/3] items-center justify-center rounded-2xl bg-zinc-100 text-zinc-400"><ImageOff className="h-10 w-10" /></div>;
    if (!src) return <div className="aspect-[4/3] skeleton rounded-2xl" />;
    return <img src={src} alt={alt} className="w-full rounded-2xl object-contain bg-zinc-50" />;
};

const Section = ({ title, children }) => (
    <div className="mb-6">
        <p className="label-uppercase mb-2">{title}</p>
        <div className="rounded-2xl border border-zinc-100 bg-white p-4">{children}</div>
    </div>
);

const Field = ({ label, value }) => (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 text-sm">
        <dt className="text-zinc-500">{label}</dt>
        <dd className="text-zinc-900">{value ?? <span className="text-zinc-400">—</span>}</dd>
    </div>
);

const Detail = () => {
    const { id } = useParams();
    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reprocessing, setReprocessing] = useState(false);
    const [refillOpen, setRefillOpen] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get(`/prescriptions/${id}`);
            setDoc(data);
        } catch {
            setDoc(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll while processing
    useEffect(() => {
        if (!doc || doc.ocr_status !== "processing") return;
        const t = setInterval(load, 2500);
        return () => clearInterval(t);
    }, [doc]); // eslint-disable-line react-hooks/exhaustive-deps

    const onReprocess = async () => {
        if (reprocessing) return;
        try {
            setReprocessing(true);
            const { data } = await api.post(`/prescriptions/${id}/reprocess`);
            setDoc(data);
            toast.success("Re-processing started");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : detail?.message || "Re-process failed");
        } finally {
            setReprocessing(false);
        }
    };

    const onDelete = async () => {
        try {
            await api.delete(`/prescriptions/${id}`);
            toast.success("Prescription deleted");
            navigate("/prescriptions");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : detail?.message || "Delete failed");
        }
    };

    if (loading) {
        return (
            <div className="grid gap-8 lg:grid-cols-2">
                <div className="aspect-[4/3] skeleton rounded-2xl" />
                <div className="space-y-3">
                    <div className="h-6 w-2/3 skeleton" />
                    <div className="h-4 w-1/2 skeleton" />
                    <div className="h-32 skeleton rounded-2xl" />
                </div>
            </div>
        );
    }

    if (!doc) {
        return (
            <div className="text-center">
                <h2 className="text-xl font-semibold">Prescription not found</h2>
                <Link to="/prescriptions" className="btn-brand mt-6 inline-flex">Back to prescriptions</Link>
            </div>
        );
    }

    const ocr = doc.ocr_data || {};
    const meds = ocr.medicines || [];
    const isFailed = doc.ocr_status === "failed";
    const isProcessing = doc.ocr_status === "processing";
    const isExtracted = doc.ocr_status === "extracted";

    return (
        <div className="grid gap-10 lg:grid-cols-[1fr_1fr]" data-testid="rx-detail-page">
            <div>
                <AuthedImage id={doc.id} alt="Prescription" />
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
                    <span>Uploaded {new Date(doc.uploaded_at).toLocaleString()}</span>
                    {doc.ocr_elapsed_seconds != null && <span>• OCR in {doc.ocr_elapsed_seconds.toFixed(1)}s</span>}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {isExtracted && (ocr.medicines || []).length > 0 && doc.verification_status !== "rejected" && (
                        <button
                            onClick={() => setRefillOpen(true)}
                            className="btn-brand !py-2 !px-4 !text-sm"
                            data-testid="rx-refill-btn"
                        >
                            <Repeat className="h-4 w-4" /> Refill these medicines
                        </button>
                    )}
                    <button
                        onClick={onReprocess}
                        disabled={reprocessing || isProcessing}
                        className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium hover:border-brand-300 disabled:opacity-50 dark:border-white/10 dark:bg-zinc-900 dark:hover:border-brand-400/40"
                        data-testid="rx-reprocess-btn"
                    >
                        <RefreshCw className={`h-4 w-4 ${reprocessing ? "animate-spin" : ""}`} />
                        Re-process
                    </button>
                    <button
                        onClick={onDelete}
                        className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-600 hover:border-red-200 hover:text-red-600 dark:border-white/10 dark:bg-zinc-900"
                        data-testid="rx-delete-btn"
                    >
                        <Trash2 className="h-4 w-4" />
                        Delete
                    </button>
                </div>
            </div>

            <div>
                <div className="mb-4 flex flex-wrap items-center gap-2">
                    <h2 className="text-2xl font-semibold tracking-tight">Extracted data</h2>
                    {isProcessing && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-amber-700">
                            <RefreshCw className="h-3 w-3 animate-spin" /> Processing
                        </span>
                    )}
                    {isExtracted && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-emerald-700">
                            <ShieldCheck className="h-3 w-3" /> Extracted
                        </span>
                    )}
                    {isFailed && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-semibold uppercase text-red-700">
                            <AlertTriangle className="h-3 w-3" /> Failed
                        </span>
                    )}
                </div>

                {isProcessing && (
                    <p className="rounded-2xl border border-zinc-100 bg-zinc-50/60 p-4 text-sm text-zinc-600" data-testid="processing-banner">
                        Our AI is reading the prescription. This page will update automatically.
                    </p>
                )}

                {isFailed && (
                    <div className="rounded-2xl border border-red-100 bg-red-50 p-4 text-sm text-red-700" data-testid="failed-banner">
                        <p className="font-medium">Extraction failed</p>
                        <p className="mt-1 text-red-600/90">{doc.ocr_error || "Try a clearer image or re-process."}</p>
                    </div>
                )}

                {isExtracted && (
                    <>
                        <Section title="Doctor">
                            <dl>
                                <Field label="Name" value={ocr.doctor_name} />
                                <Field label="Reg. No." value={ocr.doctor_registration_no} />
                                <Field label="Clinic" value={ocr.clinic_name} />
                            </dl>
                        </Section>

                        <Section title="Patient">
                            <dl>
                                <Field label="Name" value={ocr.patient_name} />
                                <Field label="Age" value={ocr.patient_age} />
                                <Field label="Gender" value={ocr.patient_gender} />
                                <Field label="Date" value={ocr.prescription_date} />
                            </dl>
                        </Section>

                        <Section title={`Medicines (${meds.length})`}>
                            {meds.length === 0 ? (
                                <p className="text-sm text-zinc-500">No medicines detected.</p>
                            ) : (
                                <ul className="divide-y divide-zinc-100">
                                    {meds.map((m, i) => (
                                        <li key={i} className="py-2 text-sm" data-testid={`med-row-${i}`}>
                                            <p className="font-medium text-zinc-900">{m.name} {m.dosage && <span className="font-normal text-zinc-500">— {m.dosage}</span>}</p>
                                            <p className="text-xs text-zinc-500">
                                                {[m.frequency, m.duration && `for ${m.duration}`, m.quantity && `Qty ${m.quantity}`].filter(Boolean).join("  •  ")}
                                            </p>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        {ocr.notes && (
                            <Section title="Notes">
                                <p className="text-sm text-zinc-700">{ocr.notes}</p>
                            </Section>
                        )}

                        <p className="text-xs text-zinc-400">
                            Confidence: {((ocr.confidence_score ?? 0) * 100).toFixed(0)}%  •  Verification: {doc.verification_status}
                        </p>
                        {(doc.suggested_lab_tests || []).length > 0 && (
                            <div className="mt-6">
                                <LabFromRxCTA tests={doc.suggested_lab_tests} context="prescription" />
                            </div>
                        )}
                    </>
                )}
            </div>
            <RefillDialog
                open={refillOpen}
                prescriptionId={doc.id}
                onClose={() => setRefillOpen(false)}
                onAddedToCart={() => navigate("/cart")}
            />

            {/* Phase 2b — Sendbird embed: chat with the pharmacist verifying this Rx */}
            <section className="mt-8" data-testid="prescription-sendbird-section">
                <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Real-time chat</p>
                        <h3 className="text-base font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Chat with pharmacist</h3>
                    </div>
                    {doc.verification_status !== "verified" && (
                        <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-semibold text-amber-800">
                            A pharmacist will respond shortly — you can leave a message now
                        </span>
                    )}
                </div>
                <SendbirdEmbeddedChannel
                    channelUrl={`pills_rx_${doc.id}`}
                    customType="rx_verification"
                    height="500px"
                    testId="sendbird-rx-embed"
                    emptyMessage="Setting up your pharmacist chat…"
                />
            </section>
        </div>
    );
};

const PrescriptionDetail = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <Link to="/prescriptions" className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-brand-700">
                <ArrowLeft className="h-4 w-4" /> Back to prescriptions
            </Link>
            <AuthGuard>
                <Detail />
            </AuthGuard>
        </section>
    </Layout>
);

export default PrescriptionDetail;
